import { useMemo, useState } from 'react';
import { PanelToolbar } from '../../components/panels/PanelToolbar';
import { SkeletonLog } from '../../components/ui/Skeleton';
import { useRtFeed } from '../../lib/rt/feed';
import type { RtChannelKey, RtSample } from '../../lib/types';
import { StripLog } from './StripLog';
import type { StripRow, StripTrack } from './StripLog';
import { FeedBadge, FeedMissing } from './FeedState';
import { hhmm, timeGrid, timeRows } from './rtFormat';

const SIDEBAR: { label: string; key: RtChannelKey; decimals: number; unit: string }[] = [
  { label: 'Hole Depth', key: 'holeDepth', decimals: 2, unit: 'm' },
  { label: 'Bit Depth', key: 'bitDepth', decimals: 2, unit: 'm' },
  { label: 'TVD', key: 'tvd', decimals: 2, unit: 'm' },
  { label: 'Block Position', key: 'blockPos', decimals: 2, unit: 'm' },
  { label: 'Rate of Penetration', key: 'rop', decimals: 1, unit: 'm/h' },
  { label: 'WOB', key: 'wob', decimals: 1, unit: 'klb' },
  { label: 'RPM', key: 'rpm', decimals: 0, unit: 'rpm' },
  { label: 'Hookload', key: 'hookload', decimals: 1, unit: 'klb' },
  { label: 'SPP', key: 'spp', decimals: 0, unit: 'psi' },
  { label: 'Torque', key: 'torque', decimals: 2, unit: 'klbf-ft' },
  { label: 'Total Gas', key: 'totalGas', decimals: 2, unit: '%' },
  { label: 'Mud Flow In', key: 'flowIn', decimals: 0, unit: 'gpm' },
  { label: 'Mud Flow Out', key: 'flowOut', decimals: 1, unit: '%' },
  { label: 'Mud Density In', key: 'densIn', decimals: 2, unit: 'ppg' },
];

const TIME_TRACKS: StripTrack[] = [
  { kind: 'axis', width: 46, title: 'Time' },
  { kind: 'activity', width: 18, title: 'Activity' },
  {
    kind: 'curves',
    width: 130,
    title: 'Drilling',
    curves: [
      { key: 'rop', label: 'ROP', unit: 'm/h', color: '#2e7d32', min: 100, max: 0 },
      { key: 'wob', label: 'WOB', unit: 'klb', color: '#c62828', min: 0, max: 50 },
      { key: 'rpm', label: 'RPM', unit: 'rpm', color: '#00838f', min: 0, max: 300, dashed: true },
    ],
  },
  {
    kind: 'curves',
    width: 110,
    title: 'Gas',
    curves: [{ key: 'totalGas', label: 'Total Gas', unit: '%', color: '#d32f2f', min: 0, max: 5 }],
  },
];

const DEPTH_TRACKS: StripTrack[] = [
  { kind: 'axis', width: 46, title: 'Depth m MD' },
  { kind: 'activity', width: 18, title: 'Mode' },
  {
    kind: 'curves',
    width: 140,
    title: 'ROP',
    curves: [
      { key: 'rop', label: 'ROP', unit: 'm/h', color: '#2e7d32', min: 100, max: 0 },
      { key: 'wob', label: 'WOB', unit: 'klb', color: '#c62828', min: 0, max: 50, dashed: true },
    ],
  },
  {
    kind: 'curves',
    width: 120,
    title: 'Torque',
    curves: [
      { key: 'torque', label: 'Torque', unit: 'klbf-ft', color: '#ef6c00', min: 0, max: 30 },
      { key: 'rpm', label: 'RPM', unit: 'rpm', color: '#00838f', min: 0, max: 300, dashed: true },
    ],
  },
  {
    kind: 'curves',
    width: 120,
    title: 'Total Gas',
    curves: [{ key: 'gas', label: 'Total Gas', unit: '%', color: '#d32f2f', min: 0, max: 5 }],
  },
];

const BIN = 0.5; // metres per depth-log row
const DEPTH_SPANS = [30, 60, 120];

/**
 * The time samples turned into a depth log, half a metre a row: the drilling
 * parameters where the bit was drilling that half metre, and the gas where
 * the mud carrying it came from, its lag depth.
 */
function depthRows(samples: RtSample[]): StripRow[] {
  const bins = new Map<number, { n: number; rop: number; wob: number; rpm: number; torque: number; slide: number; gasN: number; gas: number }>();
  const bin = (d: number) => Math.round(d / BIN) * BIN;
  const at = (d: number) => {
    let b = bins.get(d);
    if (!b) {
      b = { n: 0, rop: 0, wob: 0, rpm: 0, torque: 0, slide: 0, gasN: 0, gas: 0 };
      bins.set(d, b);
    }
    return b;
  };
  for (const s of samples) {
    if (s.activity === 'Drill-rotate' || s.activity === 'Drill-slide') {
      const b = at(bin(s.holeDepth));
      b.n++;
      b.rop += s.rop;
      b.wob += s.wob;
      b.rpm += s.rpm;
      b.torque += s.torque;
      if (s.activity === 'Drill-slide') b.slide++;
    }
    if (s.flowIn > 50) {
      const g = at(bin(s.lagDepth));
      g.gasN++;
      g.gas += s.totalGas;
    }
  }
  return [...bins.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([y, b]) => ({
      y,
      values: {
        rop: b.n ? b.rop / b.n : NaN,
        wob: b.n ? b.wob / b.n : NaN,
        rpm: b.n ? b.rpm / b.n : NaN,
        torque: b.n ? b.torque / b.n : NaN,
        gas: b.gasN ? b.gas / b.gasN : NaN,
      },
      activity: b.n === 0 ? undefined : b.slide * 2 > b.n ? 'Drill-slide' : 'Drill-rotate',
    }));
}

export function RtTimeDepth({ wellId }: { wellId: number }) {
  const feed = useRtFeed(wellId);
  const [depthSpan, setDepthSpan] = useState(60);
  const tRows = useMemo(() => timeRows(feed.samples), [feed.samples]);
  const dRows = useMemo(() => depthRows(feed.samples), [feed.samples]);

  if (feed.status === 'none' || feed.status === 'error') {
    return (
      <section className="panel panel--full" aria-label="RT Time-Depth">
        <PanelToolbar title="RT Time-Depth" accent />
        <FeedMissing feed={feed} />
      </section>
    );
  }

  const latest = feed.latest;
  const waiting = feed.status === 'connecting' && !latest;
  const timeSpan = 60;
  const { minor, major } = timeGrid(timeSpan);
  const step = (feed.info?.stepSeconds ?? 5) * 1000;
  const holeTop = latest ? latest.holeDepth + 2 : 0;

  return (
    <div className="splitpane splitpane--thirds">
      <section className="panel" aria-label="RT Timelog">
        <PanelToolbar title="RT Timelog" />
        <FeedBadge feed={feed} />
        {waiting ? (
          <SkeletonLog label="Connecting to the real-time feed" />
        ) : (
          <StripLog
            rows={tRows}
            tracks={TIME_TRACKS}
            top={Date.parse(latest!.t) + 15_000}
            span={timeSpan * 60_000}
            minor={minor}
            major={major}
            formatY={hhmm}
            gap={step * 3}
          />
        )}
      </section>

      <section className="panel" aria-label="RT Depth Log">
        <PanelToolbar title="RT DEPTH LOG" accent />
        <div className="rtspan">
          Window
          {DEPTH_SPANS.map((m) => (
            <button
              key={m}
              type="button"
              className={m === depthSpan ? 'treetool is-active' : 'treetool'}
              aria-pressed={m === depthSpan}
              onClick={() => setDepthSpan(m)}
            >
              {m} m
            </button>
          ))}
        </div>
        {waiting ? (
          <SkeletonLog label="Building the depth log" />
        ) : (
          <StripLog
            rows={dRows}
            tracks={DEPTH_TRACKS}
            top={holeTop}
            span={depthSpan}
            minor={depthSpan <= 30 ? 1 : 2}
            major={depthSpan <= 30 ? 5 : 10}
            formatY={(d) => d.toFixed(0)}
            gap={BIN * 3}
          />
        )}
      </section>

      <aside className="navalues" aria-label="Current channel values">
        {SIDEBAR.map((s) => (
          <div className="navalue" key={s.key}>
            <span className="navalue-label">
              {s.label} <small>{s.unit}</small>
            </span>
            <span className="navalue-figure">{latest ? latest[s.key].toFixed(s.decimals) : 'N/A'}</span>
          </div>
        ))}
        {latest && (
          <div className="navalue">
            <span className="navalue-label">Activity</span>
            <span className="navalue-figure navalue-figure--text">{latest.activity}</span>
          </div>
        )}
      </aside>
    </div>
  );
}
